import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Image, Music, Video, Check, Upload, Play, Pause, Volume2, FileArchive } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for media files
const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

export const MediaLibraryPicker = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  allowedTypes = ['image', 'audio', 'video'],
  multiple = false 
}) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Audio preview state
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadMedia();
      setSelected([]);
      setPlayingId(null);
    } else {
      // Stop audio when modal closes
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isOpen]);

  // Audio preview handler
  const toggleAudioPreview = (item, e) => {
    e.stopPropagation();
    
    if (playingId === item.id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    } else {
      // Stop current and play new
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(getFullUrl(item.url));
      audio.volume = 0.7;
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => {
        toast.error('Failed to play audio');
        setPlayingId(null);
      };
      audio.play().catch(err => {
        console.error('Audio play error:', err);
        toast.error('Failed to play audio');
        setPlayingId(null);
      });
      
      audioRef.current = audio;
      setPlayingId(item.id);
    }
  };

  const loadMedia = async () => {
    try {
      const response = await adminFetch('/admin/media');
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Media API error:', response.status, errorText);
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
          return;
        }
        toast.error(`Failed to load media: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setMedia(data);
      } else {
        console.error('Media API returned non-array:', data);
        setMedia([]);
      }
    } catch (error) {
      console.error('Failed to load media:', error);
      toast.error('Failed to load media. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await adminFetch('/admin/media/upload', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s)`);
      loadMedia();
    }
    setUploading(false);
    e.target.value = '';
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image')) return <Image className="w-5 h-5" />;
    if (type?.startsWith('audio')) return <Music className="w-5 h-5" />;
    if (type?.startsWith('video')) return <Video className="w-5 h-5" />;
    if (type?.includes('zip') || type?.includes('archive')) return <FileArchive className="w-5 h-5" />;
    return <Image className="w-5 h-5" />;
  };

  // Generate accept string based on allowedTypes
  const getAcceptString = () => {
    const acceptParts = [];
    if (allowedTypes.includes('image') || allowedTypes.includes('all')) acceptParts.push('image/*');
    if (allowedTypes.includes('audio') || allowedTypes.includes('all')) acceptParts.push('audio/*');
    if (allowedTypes.includes('video') || allowedTypes.includes('all')) acceptParts.push('video/*');
    if (allowedTypes.includes('archive') || allowedTypes.includes('all')) {
      acceptParts.push('.zip,.rar,.7z,application/zip,application/x-zip-compressed,application/x-rar-compressed');
    }
    return acceptParts.join(',') || '*/*';
  };

  const isTypeAllowed = (item) => {
    if (allowedTypes.includes('all')) return true;
    // Support both 'type' (simple) and 'mime_type' (full MIME) fields
    const itemType = item.mime_type || item.type || '';
    // Check for archive types
    if (allowedTypes.includes('archive') && (itemType?.includes('zip') || itemType?.includes('archive'))) {
      return true;
    }
    return allowedTypes.some(allowed => itemType?.startsWith(allowed));
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.filename?.toLowerCase().includes(searchQuery.toLowerCase());
    // Support both 'type' (simple) and 'mime_type' (full MIME) fields
    const itemType = item.mime_type || item.type || '';
    // Handle archive filter type specially
    const matchesType = filterType === 'all' || 
      (filterType === 'archive' && (itemType?.includes('zip') || itemType?.includes('archive'))) ||
      (filterType !== 'archive' && itemType?.startsWith(filterType));
    const isAllowed = isTypeAllowed(item);
    return matchesSearch && matchesType && isAllowed;
  });

  const handleSelect = (item) => {
    if (multiple) {
      setSelected(prev => 
        prev.find(s => s.id === item.id) 
          ? prev.filter(s => s.id !== item.id)
          : [...prev, item]
      );
    } else {
      // Single-pick mode: pass the single item directly (natural API).
      onSelect(item);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (multiple && selected.length > 0) {
      onSelect(selected);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Media Library</h2>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="p-4 border-b border-zinc-800 flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Types</option>
              {allowedTypes.includes('image') && <option value="image">Images</option>}
              {allowedTypes.includes('audio') && <option value="audio">Audio</option>}
              {allowedTypes.includes('video') && <option value="video">Video</option>}
              {allowedTypes.includes('archive') && <option value="archive">Archives (ZIP)</option>}
            </select>

            <label className="px-4 py-2 bg-primary hover:bg-primary/90 text-black font-bold rounded-lg cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload'}
              <input
                type="file"
                multiple
                onChange={handleUpload}
                className="hidden"
                accept={getAcceptString()}
              />
            </label>
          </div>

          {/* Media Grid */}
          <div className="p-4 overflow-y-auto max-h-[50vh]">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : filteredMedia.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No media found. Upload some files to get started.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredMedia.map(item => {
                  const isSelected = !!selected.find(s => s.id === item.id);
                  const itemType = item.mime_type || item.type || '';
                  const isAudio = itemType?.startsWith('audio');
                  const isImage = itemType?.startsWith('image');
                  const isPlaying = playingId === item.id;

                  // ---------- AUDIO TILE: clean 3-row card ----------
                  if (isAudio) {
                    return (
                      <div
                        key={item.id}
                        className={`relative rounded-xl border-2 transition-all bg-zinc-800 flex flex-col overflow-hidden ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                      >
                        {/* 1. FILENAME on top — fully readable, 2 lines */}
                        <div className="px-3 pt-3 pb-2 min-h-[3.5rem]">
                          <div className="flex items-start gap-2">
                            <Music className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <p
                              className="text-sm text-white font-medium leading-tight break-words line-clamp-2"
                              title={item.filename}
                            >
                              {item.filename}
                            </p>
                          </div>
                        </div>

                        {/* 2. PLAY PREVIEW — big, obvious, in its own middle band */}
                        <div className="flex items-center justify-center py-3 bg-zinc-900/60">
                          <button
                            data-testid={`preview-audio-${item.id}`}
                            onClick={(e) => toggleAudioPreview(item, e)}
                            title={isPlaying ? 'Stop preview' : 'Preview'}
                            className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 ${
                              isPlaying ? 'bg-green-500' : 'bg-white hover:bg-white/90'
                            }`}
                          >
                            {isPlaying ? (
                              <Pause className="w-7 h-7 text-white" />
                            ) : (
                              <Play className="w-7 h-7 text-zinc-900 ml-1" />
                            )}
                          </button>
                        </div>

                        {/* 3. SELECT — primary CTA, separate band, impossible to miss */}
                        <button
                          data-testid={`select-audio-${item.id}`}
                          onClick={(e) => { e.stopPropagation(); handleSelect(item); }}
                          className={`w-full py-3 text-sm font-bold transition-colors ${
                            isSelected
                              ? 'bg-primary text-black'
                              : 'bg-zinc-700 hover:bg-primary hover:text-black text-white'
                          }`}
                        >
                          {isSelected ? '✓ Selected' : 'Select this track'}
                        </button>
                      </div>
                    );
                  }

                  // ---------- IMAGE / OTHER TILE (unchanged behavior) ----------
                  return (
                    <div
                      key={item.id}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      <button
                        onClick={() => handleSelect(item)}
                        className="w-full h-full"
                      >
                        {isImage ? (
                          <img
                            src={getFullUrl(item.url)}
                            alt={item.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : item.cover_url ? (
                          <div className="relative w-full h-full">
                            <img
                              src={getFullUrl(item.cover_url)}
                              alt={item.filename}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : null}
                        <div
                          className="w-full h-full bg-zinc-800 flex flex-col items-center justify-center gap-2 p-3"
                          style={{ display: isImage || item.cover_url ? 'none' : 'flex' }}
                        >
                          {getFileIcon(itemType)}
                          <span className="text-sm text-zinc-200 break-words text-center line-clamp-3">
                            {item.filename}
                          </span>
                        </div>
                      </button>

                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center pointer-events-none">
                          <Check className="w-4 h-4 text-black" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {multiple && (
            <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
              <span className="text-zinc-400">
                {selected.length} item{selected.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleConfirm}
                disabled={selected.length === 0}
                className="px-6 py-2 bg-primary hover:bg-primary/90 text-black font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Insert Selected
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MediaLibraryPicker;
